import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { ModalFrame, ModalFrameFooter } from 'components/modal';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import ActionButton from 'components/buttons/action-button/ActionButton';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import TabsUnderlined from 'components/tabs/tabs-underlined/TabsUnderlined';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/spinner/Spinner';

import { fetchList, fetchListInfluencers } from 'modules/lists/reducers/lists';
import { deleteCreators, fetchCreators, resetCreators } from 'modules/lists/reducers/collections-bulk';
import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { getQueryValueFromUrl } from 'utils/list';
import { abbrNum } from 'utils/helper/common';

import './collections-bulk-delete-creators-modal.scss';

import type {
  Entity,
  List,
  ListInfluencerOptions,
  ListScreenState,
  LocationWindow
} from 'types';
import type {
  CollectionsBulkDeleteCreatorsChannelsMap,
  CollectionsBulkDeleteCreatorsItem,
  CollectionsBulkDeleteCreatorsSelectedMap
} from 'modules/lists/components/modals/bulk-delete-creators/types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./collections-bulk-delete-creators-modal.i18n.json'));

type OwnProps = {
  orgId: number,
  list: List,
  location?: LocationWindow,
  onCloseModal: () => void
};

type StateProps = {
  creators: Array<CollectionsBulkDeleteCreatorsItem>,
  screenState: ListScreenState,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchCreators: (d: number, c: number, b: string, a: number) => Promise<void>,
  dispatchDeleteCreators: (c: number, b: number, a: Array<string>) => Promise<void>,
  dispatchFetchList: (c: number, b: number, a: string) => void,
  dispatchFetchListInfluencers: (
    e: number,
    d: number,
    c: string,
    b: number | undefined | null,
    a: ListInfluencerOptions
  ) => void,
  dispatchResetCreators: () => void,
  dispatchError: (a: string) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  selectedMap: CollectionsBulkDeleteCreatorsSelectedMap,
  channelsMap: CollectionsBulkDeleteCreatorsChannelsMap
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'collections_bulk_delete_creators_modal';

const SELECT_ALL = 'select_all';
const DESELECT_ALL = 'deselect_all';
const INSTAGRAM = 'instagram';
const TIKTOK = 'tiktok';
const YOUTUBE = 'youtube';

class CollectionsBulkDeleteCreatorsModal extends React.PureComponent<Props, State> {
  state = {
    selectedMap: {},
    channelsMap: {}
  };

  componentDidMount() {
    const { orgId, list, screenState, dispatchFetchCreators } = this.props;

    dispatchFetchCreators(orgId, list.id, screenState.sort, screenState.totalItems)
      .then(this.handleSuccess)
      .catch(this.handleError);
  }

  componentWillUnmount() {
    const { dispatchResetCreators } = this.props;

    dispatchResetCreators();
  }

  getSelectedIds(): Array<string> {
    const { selectedMap } = this.state;

    return Object.keys(selectedMap)
      .filter(key => selectedMap[Number(key)]);
  }

  getToggleMsgKey(): string {
    const { creators } = this.props;
    const selectedItems = this.getSelectedIds();

    return selectedItems.length === creators.length
      ? DESELECT_ALL
      : SELECT_ALL;
  }

  getChannels(creator: CollectionsBulkDeleteCreatorsItem): Array<string> {
    const channels: Array<string> = [];

    if (!isNil(creator.instagramUrl)) {
      channels.push(INSTAGRAM);
    }

    if (!isNil(creator.tiktokUrl)) {
      channels.push(TIKTOK);
    }

    if (!isNil(creator.youtubeUrl)) {
      channels.push(YOUTUBE);
    }

    return channels;
  }

  getEntity(creator: CollectionsBulkDeleteCreatorsItem): Entity {
    const creatorId = creator.id;

    return {
      id: creatorId,
      firstName: creator.name,
      lastName: '',
      displayName: creator.name,
      image: {
        url: creator.profileImageUrl
      },
      url: `/content-creator/profiles/${creatorId}`
    };
  }

  handleSuccess = (): void => {
    const { creators } = this.props;
    const selectedMap = {};
    const channelsMap = {};

    creators.forEach(creator => {
      selectedMap[creator.id] = false;
      channelsMap[creator.id] = this.getChannels(creator);
    });

    this.setState({
      selectedMap,
      channelsMap
    });
  };

  handleError = (): void => {
    const { dispatchError } = this.props;

    dispatchError(__t(`${MSG_KEY}.messages.error_loading`));
  };

  handleToggleAll = (event: SyntheticEvent<HTMLElement>): void => {
    const { selectedMap } = this.state;
    const target = event.currentTarget;
    const toggleState = target.dataset.arg;
    const selectedKeys = Object.keys(selectedMap);
    const newState = {};

    selectedKeys.forEach(key => {
      newState[key] = toggleState === SELECT_ALL;
    });

    this.setState({
      selectedMap: newState
    });
  };

  handleToggleItem = (event: SyntheticEvent<HTMLInputElement>): void => {
    const target = event.currentTarget;
    const isChecked = target.checked;
    const creatorId = Number(target.dataset.creatorId || 0);

    if (!creatorId) {
      return;
    }

    this.setState(state => ({
      selectedMap: {
        ...state.selectedMap,
        [creatorId]: isChecked
      }
    }));
  };

  handleSubmit = (): void => {
    const {
      orgId,
      list,
      location,
      onCloseModal,
      dispatchDeleteCreators,
      dispatchFetchList,
      dispatchFetchListInfluencers
    } = this.props;

    const selectedItems = this.getSelectedIds();
    const listId = list.id;

    dispatchDeleteCreators(orgId, listId, selectedItems)
      .then(() => {
        dispatchFetchList(orgId, listId, 'all');

        if (location) {
          // used only when inside collection, but not needed for collection list view
          const name = getQueryValueFromUrl(location, 'name');
          const page = getQueryValueFromUrl(location, 'page');
          dispatchFetchListInfluencers(orgId, listId, name, Number(page), { loadCreatorApprovalStatus: true });
        }
      });

    onCloseModal();
  };

  renderSpinner(): React.ReactNode {
    return (
      <div className="collections-bulk-delete-creators-modal__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  renderConfirmation(): React.ReactNode {
    const selectedItems = this.getSelectedIds();

    const confirmationClasses = classnames('collections-bulk-delete-creators-modal__confirmation', {
      'collections-bulk-delete-creators-modal__confirmation--is-visible': !isEmpty(selectedItems)
    });

    return (
      <div className={confirmationClasses}>
        {__t(`${MSG_KEY}.messages.confirmation`)}
      </div>
    );
  }

  renderStatisticInfo(): JSX.Element {
    const { creators } = this.props;
    const selectedCreators = this.getSelectedIds();

    return (
      <div className="collections-bulk-delete-creators-modal__statistic-info">
        <span className="collections-bulk-delete-creators-modal__statistic-total">
          { __t(`${MSG_KEY}.creators_total`, { count: creators.length })}
        </span>
        <span className="collections-bulk-delete-creators-modal__statistic-selected">
          { __t(`${MSG_KEY}.creators_selected`, { count: selectedCreators.length })}
        </span>
      </div>
    );
  }

  renderSubheader(): React.ReactNode | undefined | null {
    const { isLoading } = this.props;

    if (isLoading) {
      return null;
    }

    return (
      <div className="collections-bulk-delete-creators-modal__subheader">
        {this.renderToggleLink()}
        {this.renderStatisticInfo()}
      </div>
    );
  }

  renderToggleLink(): React.ReactNode {
    const toggleMsgKey = this.getToggleMsgKey();

    const tabItems = [{
      handleClick: this.handleToggleAll,
      handleClickArg: toggleMsgKey,
      isActive: false,
      label: __t(`${MSG_KEY}.labels.${toggleMsgKey}`)
    }];

    return (
      <TabsUnderlined
        items={tabItems}
        size="small"
      />
    );
  }

  renderFooter(): React.ReactNode {
    const { isLoading, onCloseModal } = this.props;
    const selectedItems = this.getSelectedIds();

    return (
      <ModalFrameFooter className="collections-bulk-delete-creators-modal__footer">
        <ActionButton
          color="tertiary"
          onClick={onCloseModal}
        >
          {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
        </ActionButton>
        <ActionButton
          color="primary"
          disabled={isLoading || isEmpty(selectedItems)}
          onClick={this.handleSubmit}
        >
          {__t(`${MSG_KEY}.buttons.remove`)}
        </ActionButton>
      </ModalFrameFooter>
    );
  }

  renderCheckbox(creator: CollectionsBulkDeleteCreatorsItem): React.ReactNode | undefined | null {
    const { selectedMap } = this.state;
    const creatorId = creator.id;
    const status = selectedMap[creatorId];

    if (isNil(status)) {
      return null;
    }

    return (
      <div className="collections-bulk-delete-creators-modal__checkbox">
        <InputCheckbox
          key={`collections-bulk-delete-creators-modal__checkbox-${creatorId}`}
          id={`collections-bulk-delete-creators-modal__checkbox-${creatorId}`}
          name="selected"
          value={String(status)}
          checked={status}
          data={{ 'creator-id': creatorId }}
          onChange={this.handleToggleItem}
        />
      </div>
    );
  }

  renderAvatar(creator: CollectionsBulkDeleteCreatorsItem): React.ReactNode {
    const entity = this.getEntity(creator);

    return (
      <div className="collections-bulk-delete-creators-modal__avatar">
        <EntityAvatar
          bordered
          entity={entity}
          link={entity.url}
          size="big"
          customSize={{
            width: 48,
            height: 48
          }}
          openInNewTab
        />
      </div>
    );
  }

  renderChannelIcon(creator: CollectionsBulkDeleteCreatorsItem, channel: string): React.ReactNode {
    const url = creator[`${channel}Url`];
    const iconProps = {
      name: `${channel}-no-customisation`,
      width: 16,
      height: 16,
      link: url,
      clickable: Boolean(url),
      openInNewTab: Boolean(url),
      onClick: (event) => event.stopPropagation()
    };

    return (
      <div className="collections-bulk-delete-creators-modal__icon">
        <SVGIcon
          {...iconProps}
        />
      </div>
    );
  }

  renderMetric(creator: CollectionsBulkDeleteCreatorsItem, channel: string): React.ReactNode {
    const metric = creator[`${channel}Followers`];
    let username = creator[`${channel}Username`] || '';

    if (channel !== YOUTUBE) {
      username = `@${username}`;
    }

    if (!isNil(metric)) {
      return (
        <Tooltip
          key={`collections-bulk-delete-creators-modal__tooltip--${creator.id}-${channel}`}
          message={`${__t(`${COMMON_MSG_KEY}.social.${channel}`)}:<br/>${username}`}
          width="short"
          display="flex"
        >
          {abbrNum(metric)}
        </Tooltip>
      );
    }

    return (
      <React.Fragment>
        –
      </React.Fragment>
    );
  }

  renderDetails(creator: CollectionsBulkDeleteCreatorsItem): React.ReactNode | undefined | null {
    const { channelsMap } = this.state;

    if (isEmpty(channelsMap)) {
      return null;
    }

    const entity = this.getEntity(creator);

    return (
      <div className="collections-bulk-delete-creators-modal__details">
        <div className="collections-bulk-delete-creators-modal__name">
          <EntityName
            entity={entity}
            link={entity.url}
            openInNewTab
          />
        </div>
        <div className="collections-bulk-delete-creators-modal__channels">
          {
            channelsMap[creator.id].map(channel => (
              <div
                key={`collections-bulk-delete-creators-modal__channel--${creator.id}-${channel}`}
                className="collections-bulk-delete-creators-modal__channel"
              >
                {this.renderChannelIcon(creator, channel)}
                <div className="collections-bulk-delete-creators-modal__metric">
                  {this.renderMetric(creator, channel)}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    );
  }

  renderCreator = (creator: CollectionsBulkDeleteCreatorsItem): JSX.Element => (
    <div
      key={`collections-bulk-delete-creators-modal__creator--${creator.id}`}
      className="collections-bulk-delete-creators-modal__creator"
    >
      {this.renderCheckbox(creator)}
      <div className="collections-bulk-delete-creators-modal__info">
        {this.renderAvatar(creator)}
        {this.renderDetails(creator)}
      </div>
    </div>
  );

  renderCreators(): React.ReactNode {
    const { creators, isLoading } = this.props;

    return (
      <div className="collections-bulk-delete-creators-modal__creators">
        {
          isLoading
            ? this.renderSpinner()
            : creators.map(this.renderCreator)
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { list } = this.props;

    return (
      <div className="collections-bulk-delete-creators-modal">
        <ModalFrame
          title={__t(`${MSG_KEY}.title`, { name: list.label })}
          subtitle={__t(`${MSG_KEY}.subtitle`)}
        >
          {this.renderSubheader()}
          {this.renderCreators()}
          {this.renderConfirmation()}
          {this.renderFooter()}
        </ModalFrame>
      </div>
    );
  }
}

const mapStateToProps = ({ collectionsBulk, lists }): StateProps => ({
  creators: collectionsBulk.creators,
  screenState: lists.screenState,
  isLoading: collectionsBulk.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCreators: (orgId: number, collectionId: number, sort: string, totalItems: number) => (
    dispatch(fetchCreators(orgId, collectionId, sort, totalItems))
  ),

  dispatchDeleteCreators: (orgId: number, collectionId: number, creatorIds: Array<string>) => (
    dispatch(deleteCreators(orgId, collectionId, creatorIds))
  ),

  dispatchFetchList: (orgId: number, listId: number, channel: string): void => dispatch(fetchList(orgId, listId, channel === 'all' ? null : channel)),

  dispatchFetchListInfluencers: (
    orgId: number,
    listId: number,
    name: string,
    pageOverride: number | undefined | null,
    options: ListInfluencerOptions
  ): void => dispatch(fetchListInfluencers(orgId, listId, name, pageOverride, options)),

  dispatchResetCreators: () => (
    dispatch(resetCreators())
  ),

  dispatchError: (message: string) => (
    dispatch(danger(message))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CollectionsBulkDeleteCreatorsModal);
